/*
 * @author  Agence Dn'D <contact@dnd.fr>
 * @license https://opensource.org/licenses/osl-3.0.php  Open Software License (OSL 3.0)
 * @link    https://www.dnd.fr/
 */

/*
 * Back in Stock (Klaviyo) form.
 *
 * Captures the custom form's submit and posts the email + variant to Klaviyo's
 * onsite back-in-stock endpoint. The theme's generic Klaviyo onsite script only
 * loads the library; it does not wire a custom form, so this glue is required.
 *
 * The public API key is read from the `data-klaviyo-api-key` attribute, and
 * falls back to the company_id of the loaded klaviyo.js (app embed).
 */
if (!customElements.get('klaviyo-back-in-stock')) {
  customElements.define(
    'klaviyo-back-in-stock',
    class KlaviyoBackInStock extends HTMLElement {
      connectedCallback() {
        this.apiKey = this.dataset.klaviyoApiKey || this.detectApiKey();

        // No resolvable Klaviyo key → the form could never submit, so hide it.
        if (!this.apiKey) {
          this.hidden = true;
          return;
        }

        this.listId = this.dataset.klaviyoListId;
        this.variantId = this.dataset.variantId;
        this.successMsg = this.dataset.success;
        this.errorMsg = this.dataset.error;

        this.emailInput = this.querySelector('.klaviyo-bis__email');
        this.newsletterInput = this.querySelector('.klaviyo-bis__newsletter-input');
        this.submitBtn = this.querySelector('.klaviyo-bis__submit');
        this.errorEl = this.querySelector('.klaviyo-bis__error');
        this.successEl = this.querySelector('.klaviyo-bis__success');
        this.wrapper = this.querySelector('.klaviyo-bis__wrapper');

        if (this.submitBtn) {
          this.submitBtn.addEventListener('click', this.onSubmit.bind(this));
        }
        if (this.emailInput) {
          this.emailInput.addEventListener('keydown', (event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              this.onSubmit();
            }
          });
        }
      }

      detectApiKey() {
        const script = document.querySelector('script[src*="klaviyo.js"]');
        if (!script) return '';
        const match = script.src.match(/company_id=([^&]+)/);
        return match ? match[1] : '';
      }

      showError(message) {
        if (!this.errorEl) return;
        this.errorEl.textContent = message || this.errorMsg;
        this.errorEl.hidden = false;
      }

      clearError() {
        if (!this.errorEl) return;
        this.errorEl.hidden = true;
        this.errorEl.textContent = '';
      }

      isValidEmail(email) {
        return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email);
      }

      onSubmit() {
        this.clearError();

        const email = this.emailInput?.value.trim() ?? '';
        if (!this.isValidEmail(email) || !this.apiKey || !this.variantId) {
          this.showError(this.errorMsg);
          return;
        }

        this.submitBtn.disabled = true;

        const companyId = encodeURIComponent(this.apiKey);
        const headers = { 'Content-Type': 'application/json', revision: '2024-10-15' };

        // Back-in-stock subscription (Klaviyo Client API — CORS enabled).
        const backInStockBody = {
          data: {
            type: 'back-in-stock-subscription',
            attributes: {
              channels: ['EMAIL'],
              profile: { data: { type: 'profile', attributes: { email: email } } },
            },
            relationships: {
              variant: {
                data: { type: 'catalog-variant', id: '$shopify:::$default:::' + this.variantId },
              },
            },
          },
        };

        // Optional newsletter opt-in (best-effort, doesn't affect the result).
        if (this.newsletterInput?.checked && this.listId) {
          const listBody = {
            data: {
              type: 'subscription',
              attributes: {
                profile: { data: { type: 'profile', attributes: { email: email } } },
              },
              relationships: { list: { data: { type: 'list', id: this.listId } } },
            },
          };
          fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + companyId, {
            method: 'POST',
            headers: headers,
            body: JSON.stringify(listBody),
          }).catch(() => {});
        }

        fetch('https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=' + companyId, {
          method: 'POST',
          headers: headers,
          body: JSON.stringify(backInStockBody),
        })
          .then((response) => {
            if (response.ok) {
              if (this.wrapper) this.wrapper.hidden = true;
              if (this.successEl) {
                this.successEl.textContent = this.successMsg;
                this.successEl.hidden = false;
              }
              return;
            }
            return response
              .json()
              .catch(() => null)
              .then((data) => {
                const detail = data && data.errors && data.errors[0] && data.errors[0].detail;
                this.showError(detail);
              });
          })
          .catch(() => this.showError())
          .finally(() => {
            this.submitBtn.disabled = false;
          });
      }
    }
  );
}
